import { Link, graphql, navigate } from 'gatsby';
import 'components/Webinars/styles/WebinarPost.css';
import useStaticFooter from 'hooks/useStaticFooter';
import useStaticHeader from 'hooks/useStaticHeader';
import Cookies from 'js-cookie';
import SEO from 'layouts/Seo';
import { useEffect, useState } from 'react';
import twMerge from 'twMerge';

import Button from 'molecules/Button';
import Section from 'molecules/Section';

import Footer from 'components/Footer';
import ComponentForm from 'components/Form';
import Header from 'components/Header';
import WebinarPostHero from 'components/Hero/PostHero/WebinarPostHero';
import headsetIcon from 'components/Webinars/assets/headset-icon.svg';
import SpeakersCarousel from 'components/Webinars/components/SpeakersCarousel';
import {
  containerStyles,
  headshotStyles,
  speakerCardStyles,
  titleStyles,
} from 'components/Webinars/styles/WebinarPost.styles';

import { formatDate } from 'utils/formatDate';
import richTextParser from 'utils/richTextParser/richTextParser';

import type { FooterProps } from 'components/Footer';
import type { HeaderProps } from 'components/Header';
import type { PageProps } from 'gatsby';
import type { ContentfulComponentForm, ContentfulTemplateWebinar } from 'graphqlTypes';
import type { FC } from 'react';

interface WebinarPostProps {
  defaultForm?: ContentfulComponentForm;
  webinarData: ContentfulTemplateWebinar;
}

type pageContextProps = {
  pageNumber: number;
  resourceType: string;
  slug: string;
  tagName?: string;
};

const WebinarPost: FC<PageProps<WebinarPostProps>> = ({ data: { defaultForm, webinarData } }) => {
  const { header } = useStaticHeader();
  const { footer } = useStaticFooter();
  const [formSubmitted, setFormSubmitted] = useState(false);

  const [speakerIndex, setSpeakerIndex] = useState(0);

  const { eventId, eventKey, body, title, date, keyTakeaways, onDemandPage, speaker, form: customForm } = webinarData;

  const isOn24 = onDemandPage && eventId && eventKey;
  const webinarForm = customForm || defaultForm;
  const formattedDate = `${formatDate(date)} PST`;

  useEffect(() => {
    const submittedCookie = Cookies.get('resourceFormSubmitted');

    setFormSubmitted(!!submittedCookie);
  }, []);

  const on24Redirect = (firstName: string, lastName: string, email: string) =>
    navigate(
      `https://event.on24.com/interface/registration/autoreg/index.html?eventid=${eventId}&sessionid=1&key=${eventKey}&firstname=${firstName}&lastname=${lastName}&email=${email}`,
    );

  const handleButtonClick = () => {
    const firstName = Cookies.get('firstName');
    const lastName = Cookies.get('lastName');
    const email = Cookies.get('email');

    if (isOn24) {
      on24Redirect(firstName, lastName, email);
    }

    const redirect = customForm?.redirectUrl?.redirectUrl;
    if (!!customForm && redirect) {
      navigate(redirect);
    }
  };

  const handleFormSuccess = values => {
    const firstName = values?.fields?.find(({ name }) => name === 'firstname')?.value;
    const lastName = values?.fields?.find(({ name }) => name === 'lastname')?.value;
    const email = values?.fields?.find(({ name }) => name === 'email')?.value;

    Cookies.set('resourceFormSubmitted', 'true');
    Cookies.set('firstName', firstName);
    Cookies.set('lastName', lastName);
    Cookies.set('email', email);

    if (isOn24) {
      on24Redirect(firstName, lastName, email);
    }

    if (customForm) {
      return;
    }

    navigate('/ty/webinar');
  };

  return (
    <div>
      {header && <Header {...(header as HeaderProps)} />}
      <main>
        <WebinarPostHero post={webinarData} />
        <Section containerClass={twMerge(containerStyles())}>
          <div className="left-content flex-1">
            <div className="hero flex flex-col gap-2">
              <p className="font-bold text-blue-700">{onDemandPage ? 'ON-DEMAND WEBINAR' : 'Live Webinar'}</p>
              <h2 className={twMerge(titleStyles())}>{title}</h2>
              <div className="date flex items-center">
                <img src={headsetIcon} className="mr-2" alt="headshot" />
                <p className="text-sm font-bold md:text-md">{formattedDate}</p>
              </div>
            </div>
            <div className="form mt-10 flex w-full bg-white p-4 shadow-lg md:hidden md:w-[25vw] md:p-6 lg:w-[370px]">
              {formSubmitted && onDemandPage ? (
                <div className="w-full">
                  <h3 className="text-xs font-bold text-gray-900 md:text-lg">Watch the webinar on-demand!</h3>
                  <Button onClick={() => handleButtonClick()} styles="mt-5 w-full">
                    View Webinar
                  </Button>
                </div>
              ) : (
                <ComponentForm {...webinarForm} onSuccess={handleFormSuccess} />
              )}
            </div>
            <div className="guests my-10 flex flex-col gap-2">
              <p className="mb-4 text-lg font-bold">Guest Speakers</p>
              <div className="flex flex-col flex-wrap gap-10 lg:flex-row">
                {speaker?.map(({ fullName, position, headshot }, index) => (
                  <Link
                    to="#speakers"
                    className={twMerge(speakerCardStyles())}
                    key={index}
                    onClick={() => setSpeakerIndex(index)}
                  >
                    {headshot && <img src={headshot.file.url} className={twMerge(headshotStyles())} alt="headshot" />}
                    <div className="flex flex-1 flex-col overflow-hidden">
                      {fullName && <p className="text-nowrap text-md font-bold">{fullName}</p>}
                      {position && <p className="overflow-hidden text-ellipsis text-nowrap text-md">{position}</p>}
                      <p className="absolute bottom-3 mt-3 text-md font-bold">Learn More</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
            <div className="learn mb-10 w-full bg-gray-100 p-5">
              <p className="mb-6 text-md font-bold">{"What you'll learn"}</p>
              <ul className="list-disc pl-5">
                {keyTakeaways?.map((takeaway, index) => (
                  <li className="mb-1 ml-3 text-lg" key={index}>
                    {takeaway}
                  </li>
                ))}
              </ul>
            </div>
            <div className="body [&>p]:text-md">{richTextParser(body as RichText)}</div>
          </div>
          <div className="form sticky top-40 hidden h-max w-full bg-white p-4 shadow-lg md:flex md:w-[25vw] md:p-6 lg:w-[370px]">
            {formSubmitted && onDemandPage ? (
              <div className="w-full">
                <h3 className="text-xs font-bold text-gray-900 md:text-lg">Watch the webinar on-demand!</h3>
                <Button onClick={() => handleButtonClick()} styles="mt-5 w-full">
                  View Webinar
                </Button>
              </div>
            ) : (
              <ComponentForm {...webinarForm} onSuccess={handleFormSuccess} />
            )}
          </div>
        </Section>
        <SpeakersCarousel speakers={speaker} activeIndex={speakerIndex} />
      </main>
      {footer && <Footer {...(footer as FooterProps)} background="blue900" padding="small" contained />}
    </div>
  );
};

export const Head: FC<PageProps<WebinarPostProps>> = ({ data: { webinarData }, pageContext }) => {
  const seo = webinarData?.seo;
  const { resourceType, slug } = pageContext as pageContextProps;

  return <SEO {...seo} slug={`${resourceType}/${slug}`} />;
};

export default WebinarPost;

export const postQuery = graphql`
  query templateWebinarPostQuery($slug: String!) {
    webinarData: contentfulTemplateWebinar(slug: { eq: $slug }) {
      ...contentfulTemplateWebinar
    }
    defaultForm: contentfulComponentForm(contentful_id: { eq: "3rGmAh7orqJvV0Ty2M9KGQ" }) {
      ...contentfulComponentForm
    }
  }
`;
